import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { assets, CONTACT } from "@/lib/assets";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Experience", to: "/team" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,box-shadow] duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl shadow-[0_8px_30px_-12px_rgba(9,30,54,0.18)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2 shrink-0">
          <img
            src={scrolled ? assets.logo : assets.logoWhite}
            alt="AGE-T Management Consulting"
            className="h-9 w-auto transition-opacity duration-300"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-sm font-medium tracking-wide transition-colors duration-300 ${
                  scrolled ? "text-navy" : "text-white"
                } ${isActive ? "opacity-100" : "opacity-70 hover:opacity-100"}`
              }
            >
              {({ isActive }) => (
                <span className="relative inline-block">
                  {item.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-1.5 left-0 right-0 h-0.5 bg-emerald-brand"
                    />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <a
          href={`tel:${CONTACT.phone}`}
          data-testid="navbar-cta"
          className="hidden md:inline-flex items-center gap-2 rounded-full bg-emerald-brand px-6 py-3 text-sm font-semibold text-navy-900 transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_24px_-8px_rgba(16,185,129,0.6)] hover:bg-emerald-light"
        >
          <Phone size={16} strokeWidth={2.5} />
          Get in Touch
        </a>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="navbar-hamburger"
          aria-label="Toggle menu"
          className={`md:hidden p-2 rounded-lg transition-colors ${scrolled ? "text-navy" : "text-white"}`}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-navy-900"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `text-2xl font-heading font-semibold ${isActive ? "text-emerald-brand" : "text-white/90"}`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <a
                href={`tel:${CONTACT.phone}`}
                data-testid="mobile-navbar-cta"
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-emerald-brand px-6 py-4 text-base font-semibold text-navy-900"
              >
                <Phone size={18} strokeWidth={2.5} /> Get in Touch
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
